import React, { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, PackagePlus, Ruler } from 'lucide-react';
import { Button } from '../Button';
import {
    PLOT_ROLLE_MAX_MM, PLOT_ROLLE_MIN_NEU_MM, PLOT_ROLLE_STANDARD_MM, laengeParsen, meterText, plotRolleErfassen,
    type PlotRolleArt, type PlotRollenStand,
} from '../../services/plotter/plotRolle';

/**
 * Papierrolle des Plotters erfassen (SQL Nr. 169/170) — für Admins im
 * Plotter-Dialog (`kompakt`) und in den Einstellungen.
 *
 *  - „Neue Rolle eingelegt“: Länge in Metern, vorbelegt mit der Standardrolle.
 *    Steht auf der alten Rolle laut Zählung noch mehr als 1 m, fragt der
 *    Knopf inline nach — ein versehentlicher Klick schriebe sonst den Rest ab.
 *  - „Restlänge korrigieren“: gemessene Restlänge; ab ihr zählt der Server neu.
 *    Ohne erfasste Rolle heißt der Knopf „Eingelegte Rolle erfassen“ und legt
 *    ebenfalls eine Korrektur an (eine angebrochene Rolle ist keine neue).
 *
 * Gesperrt wird erst, wenn eine Rolle erfasst ist. Speichern ist gegen
 * Doppelklick gesperrt; Fehler stehen inline. Ohne `darf_erfassen` nichts.
 */

export interface PlotRolleErfassenProps {
    /** Aktueller Stand (rolle evtl. null). */
    stand: PlotRollenStand;
    onGespeichert: (neu: PlotRollenStand) => void;
    /** Kompakt im Plotter-Dialog: zwei kleine Knöpfe, Formular klappt inline auf. */
    kompakt?: boolean;
}

const BEMERKUNG_MAX = 500;
/** Ab so viel Rest auf der alten Rolle fragt „Neue Rolle eingelegt“ nach. */
const REST_NACHFRAGE_MM = 1_000;

const INPUT = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-teal-500';

const fehlerText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** 20000 → „20“, 12500 → „12,5“ — Meter ohne Einheit, wie man sie eintippt. */
const meterEingabe = (mm: number): string =>
    (Math.round(mm) / 1000).toLocaleString('de-DE', { maximumFractionDigits: 3, useGrouping: false });

/** „0“, „0,0 m“, „0 cm“: eine leere Rolle. `laengeParsen` kennt nur Längen über 0. */
const NULL_LAENGE = /^\s*0+(?:[.,]0*)?\s*(?:m|cm|mm)?\s*$/i;

type Pruefung = { mm: number; fehler: '' } | { mm: null; fehler: string };

/** Eingabe gegen die Grenzen aus SQL Nr. 170 (`plot_rolle_erfassen`). */
const laengePruefen = (art: PlotRolleArt, eingabe: string): Pruefung => {
    if (!eingabe.trim()) return { mm: null, fehler: 'Bitte die Länge in Metern eingeben.' };
    const mm = art === 'korrektur' && NULL_LAENGE.test(eingabe) ? 0 : laengeParsen(eingabe);
    if (mm === null) return { mm: null, fehler: 'Keine gültige Länge — z. B. 20 oder 12,5 m.' };
    const bis = meterEingabe(PLOT_ROLLE_MAX_MM);
    if (art === 'neu' && (mm < PLOT_ROLLE_MIN_NEU_MM || mm > PLOT_ROLLE_MAX_MM)) {
        return { mm: null, fehler: `Die Rollenlänge muss zwischen ${meterEingabe(PLOT_ROLLE_MIN_NEU_MM)} und ${bis} m liegen.` };
    }
    if (art === 'korrektur' && (mm < 0 || mm > PLOT_ROLLE_MAX_MM)) {
        return { mm: null, fehler: `Die Restlänge muss zwischen 0 und ${bis} m liegen.` };
    }
    return { mm, fehler: '' };
};

export const PlotRolleErfassen: React.FC<PlotRolleErfassenProps> = ({ stand, onGespeichert, kompakt = false }) => {
    const [modus, setModus] = useState<PlotRolleArt | null>(null);
    const [eingabe, setEingabe] = useState('');
    const [bemerkung, setBemerkung] = useState('');
    // Erst nach einem Speicherversuch oder einer Eingabe die Prüfmeldung zeigen.
    const [versucht, setVersucht] = useState(false);
    const [nachfrage, setNachfrage] = useState(false);
    const [speichert, setSpeichert] = useState(false);
    const [fehler, setFehler] = useState('');
    const [erfolg, setErfolg] = useState('');

    const speichertRef = useRef(false);
    const aktivRef = useRef(true);
    const idLaenge = useId();
    const idBemerkung = useId();

    useEffect(() => {
        aktivRef.current = true;
        return () => { aktivRef.current = false; };
    }, []);

    if (!stand.darf_erfassen) return null;

    const mitRolle = stand.rolle !== null;
    const korrekturTitel = mitRolle ? 'Restlänge korrigieren' : 'Eingelegte Rolle erfassen';
    const pruefung = modus ? laengePruefen(modus, eingabe) : null;
    const zeigeFehler = !!pruefung && pruefung.mm === null && (versucht || eingabe.trim() !== '');
    const restAlt = stand.rest_mm;

    const oeffnen = (art: PlotRolleArt) => {
        setModus(art);
        setEingabe(art === 'neu' ? meterEingabe(stand.standard_laenge_mm || PLOT_ROLLE_STANDARD_MM) : '');
        setBemerkung('');
        setVersucht(false);
        setNachfrage(false);
        setFehler('');
        setErfolg('');
    };

    const schliessen = () => {
        if (speichertRef.current) return;
        setModus(null);
        setNachfrage(false);
        setFehler('');
    };

    const speichern = async (bestaetigt = false) => {
        if (speichertRef.current || !modus) return;
        setVersucht(true);
        const p = laengePruefen(modus, eingabe);
        if (p.mm === null) return;
        if (modus === 'neu' && !bestaetigt && mitRolle && restAlt !== null && restAlt > REST_NACHFRAGE_MM) {
            setNachfrage(true);
            return;
        }
        const art = modus;
        speichertRef.current = true;
        setSpeichert(true);
        setFehler('');
        try {
            const neu = await plotRolleErfassen(art, p.mm, bemerkung.trim() || null);
            onGespeichert(neu);
            if (!aktivRef.current) return;
            setModus(null);
            setNachfrage(false);
            setErfolg(art === 'neu' ? `Neue Rolle erfasst: ${meterText(p.mm)}.` : `Restlänge erfasst: ${meterText(p.mm)}.`);
        } catch (e) {
            if (aktivRef.current) setFehler(fehlerText(e));
        } finally {
            speichertRef.current = false;
            if (aktivRef.current) setSpeichert(false);
        }
    };

    const knopfKlasse = kompakt ? 'min-h-[40px]' : '';

    return (
        <div className={kompakt ? 'space-y-2' : 'space-y-3'}>
            {!modus && (
                <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" size={kompakt ? 'sm' : 'md'} className={knopfKlasse} onClick={() => oeffnen('neu')}
                        icon={<PackagePlus className="w-4 h-4" />}>
                        Neue Rolle eingelegt
                    </Button>
                    <Button variant={kompakt ? 'ghost' : 'secondary'} size={kompakt ? 'sm' : 'md'} className={knopfKlasse}
                        onClick={() => oeffnen('korrektur')} icon={<Ruler className="w-4 h-4" />}>
                        {korrekturTitel}
                    </Button>
                </div>
            )}

            {!modus && erfolg && (
                <p role="status" className="flex items-center gap-1.5 text-xs text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> {erfolg}
                </p>
            )}

            {modus && pruefung && (
                <div className={`rounded-xl border border-slate-200 bg-slate-50 ${kompakt ? 'p-3 space-y-2' : 'p-4 space-y-3'}`}>
                    <p className="text-sm font-bold text-slate-700">{modus === 'neu' ? 'Neue Rolle eingelegt' : korrekturTitel}</p>
                    {modus === 'korrektur' && (
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                            {mitRolle && restAlt !== null
                                ? `Laut Zählung noch ${meterText(restAlt)}. Ab der gemessenen Länge wird neu gezählt.`
                                : 'Die gemessene Länge der eingelegten Rolle. Ab jetzt zählt die App mit und sperrt, wenn sie für einen Plan nicht mehr reicht.'}
                        </p>
                    )}
                    <div>
                        <label htmlFor={idLaenge} className="block text-[11px] font-bold text-slate-500 uppercase mb-1">
                            {modus === 'neu' ? 'Länge der neuen Rolle (m)' : 'Gemessene Restlänge (m)'}
                        </label>
                        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                            <input
                                id={idLaenge}
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                value={eingabe}
                                placeholder={modus === 'neu' ? '20' : 'z. B. 7,5'}
                                onChange={(e) => { setEingabe(e.target.value); setNachfrage(false); setFehler(''); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void speichern(); } }}
                                disabled={speichert}
                                aria-invalid={zeigeFehler || undefined}
                                className={`${INPUT} ${kompakt ? 'sm:w-32' : 'sm:w-40'} flex-1 sm:flex-none min-w-0`}
                            />
                            {pruefung.mm !== null && (
                                <span className="text-xs text-slate-500 tabular-nums">= {meterText(pruefung.mm)}</span>
                            )}
                        </div>
                        {zeigeFehler && <p className="text-xs text-red-700 mt-1">{pruefung.fehler}</p>}
                    </div>
                    <div>
                        <label htmlFor={idBemerkung} className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Bemerkung (optional)</label>
                        <input
                            id={idBemerkung}
                            type="text"
                            value={bemerkung}
                            maxLength={BEMERKUNG_MAX}
                            placeholder="z. B. Lieferung 10/2026"
                            onChange={(e) => setBemerkung(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void speichern(); } }}
                            disabled={speichert}
                            className={INPUT}
                        />
                    </div>

                    {nachfrage && restAlt !== null && (
                        <div role="alert" className="flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                            <p className="flex items-start gap-1.5 text-xs text-amber-900 flex-1 min-w-[12rem]">
                                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                Die alte Rolle hat laut Zählung noch {meterText(restAlt)} — trotzdem als neue Rolle zählen?
                            </p>
                            <Button variant="ghost" size="sm" onClick={() => setNachfrage(false)} disabled={speichert}>Abbrechen</Button>
                            <Button variant="primary" size="sm" onClick={() => { void speichern(true); }} loading={speichert}>
                                Ja, als neue Rolle zählen
                            </Button>
                        </div>
                    )}

                    {fehler && <p role="alert" className="text-xs text-red-700">{fehler}</p>}

                    {!nachfrage && (
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button variant="ghost" size="sm" className={knopfKlasse} onClick={schliessen} disabled={speichert}>Abbrechen</Button>
                            <Button variant="primary" size="sm" className={knopfKlasse} onClick={() => { void speichern(); }} loading={speichert}
                                disabled={zeigeFehler}>
                                Speichern
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
